import { cloneElement, useCallback, useEffect, useMemo, useState } from 'react';
import { onlyText } from 'react-children-utilities';
import { useResizeDetector } from 'react-resize-detector';

import Typography from '@material-ui/core/Typography';
import makeStyles from '@material-ui/core/styles/makeStyles';

import { Tooltip } from '../../components/tooltip/Tooltip';

const useEllipsisTooltipStyles = makeStyles<import('@material-ui/core/styles').Theme, { tooltipMaxWidth?: number }>({
  tooltipMaxWidth: {
    maxWidth: ({ tooltipMaxWidth }) => tooltipMaxWidth,
  },
});

type UseEllipsisProps = {
  text: JSX.Element | string;
  /** Max width (px) for the tooltip when text is truncated. Default: 320 */
  tooltipMaxWidth?: number;
};

export const useEllipsis = ({ text, tooltipMaxWidth }: UseEllipsisProps) => {
  const tooltipStyles = useEllipsisTooltipStyles({ tooltipMaxWidth });
  const { width, ref } = useResizeDetector();

  const [content, setContent] = useState<JSX.Element | string>();

  const isEllipsis = useMemo(
    () => !!ref.current && ref.current.offsetWidth < ref.current.scrollWidth,
    [width, content],
  );

  useEffect(() => {
    setContent(typeof text === 'string' ? text : onlyText(text.props.children));
  }, [text]);

  const renderEllipsisText = useCallback(() => {
    return (
      <Tooltip
        isClick={!isEllipsis}
        isArrow
        interactive
        placement="top"
        zIndex={1300}
        enterTouchDelay={0}
        title={<Typography variant="body1">{typeof text === 'string' ? text : text.props.children}</Typography>}
        classes={{
          tooltip: tooltipStyles.tooltipMaxWidth,
        }}
      >
        {typeof text === 'string' ? (
          <Typography variant="body1" noWrap ref={ref}>
            {text}
          </Typography>
        ) : (
          cloneElement(text, {
            style: {
              // Preserve any style the caller set on the element (e.g. color/font
              // to inherit from a parent) — the truncation rules are layered on
              // top, not used to replace it.
              ...(text.props as { style?: React.CSSProperties }).style,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            },
            ref,
          })
        )}
      </Tooltip>
    );
  }, [isEllipsis, ref, text, tooltipStyles.tooltipMaxWidth]);

  return { isEllipsis, renderEllipsisText };
};
